import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Tabs,
  Tab,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CodeEditor from "../common/code-editor";

const SchemaConfigDialog = ({
  open,
  onClose,
  currentSchema,
  onSaveConfig,
  onSaveScript,
  prodConfig,
  setProdConfig,
  stagingConfig,
  setStagingConfig,
  details,
  setDetails,
}) => {
  const [schemaTab, setSchemaTab] = useState(0);
  const [envTab, setEnvTab] = useState(0);
  const currentEnvRef = useRef(0);
  const [configCode, setConfigCode] = useState("");
  const [diffConfigCode, setDiffConfigCode] = useState("");

  // Update config codes when dialog opens or environment changes
  useEffect(() => {
    if (open && currentSchema?.type === "config") {
      if (envTab === 0) {
        setConfigCode(prodConfig.latest || "");
        setDiffConfigCode(prodConfig.inUse || "");
      } else {
        setConfigCode(stagingConfig.latest || "");
        setDiffConfigCode(stagingConfig.inUse || "");
      }
    }
  }, [open, currentSchema, envTab, prodConfig, stagingConfig]);

  // Set initial schema tab when details change
  useEffect(() => {
    if (details.length > 0) {
      const inUseIndex = details.findIndex(detail => detail.inUse);
      if (inUseIndex !== -1) {
        setSchemaTab(inUseIndex);
      }
    }
  }, [details]);

  const handleSchemaTabChange = (event, newValue) => {
    setSchemaTab(newValue);
  };

  const handleEnvTabChange = (event, newValue) => {
    setEnvTab(newValue);
    currentEnvRef.current = newValue;
    // Update the displayed configs based on the selected environment
    if (newValue === 0) {
      setConfigCode(prodConfig.latest);
      setDiffConfigCode(prodConfig.inUse);
    } else {
      setConfigCode(stagingConfig.latest);
      setDiffConfigCode(stagingConfig.inUse);
    }
  };

  const handleClose = () => {
    setConfigCode("");
    setDiffConfigCode("");
    setSchemaTab(0);
    setEnvTab(0);
    onClose();
  };

  const handleSave = () => {
    if (currentSchema?.type === "config") {
      onSaveConfig(envTab);
    } else {
      onSaveScript();
    }
  };

  if (!currentSchema) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {currentSchema.type === "config" ? "Config" : "Script"}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: theme => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {currentSchema.type === "script" ? (
          <Box>
            <Box sx={{ flexGrow: 1 }}>
              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs
                  value={schemaTab}
                  onChange={handleSchemaTabChange}
                  aria-label="Script Tab"
                >
                  {details.map((detail, index) => (
                    <Tab
                      key={index}
                      label={
                        detail.inUse
                          ? "Current"
                          : detail.draft
                          ? "Draft"
                          : `Version ${detail.v}`
                      }
                    />
                  ))}
                </Tabs>
              </Box>
              {details.map(
                (detail, index) =>
                  schemaTab === index && (
                    <Box key={index} sx={{ flexGrow: 1 }}>
                      <CodeEditor
                        value={detail.schema}
                        editable={true}
                        parseResults={detail.parseResults || []}
                        height="800px"
                        onChange={instance => {
                          const updatedDetails = [...details];
                          updatedDetails[index] = {
                            ...updatedDetails[index],
                            schema: instance.getValue(),
                            isModified: true,
                          };
                          setDetails(updatedDetails);
                        }}
                      />
                    </Box>
                  ),
              )}
            </Box>
          </Box>
        ) : (
          <Box>
            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
              <Tabs
                value={envTab}
                onChange={handleEnvTabChange}
                aria-label="Environment Tabs"
              >
                <Tab label="Production" />
                <Tab label="Staging" />
              </Tabs>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="subtitle1" gutterBottom>
                  Latest Configuration
                </Typography>
                <CodeEditor
                  value={configCode}
                  editable={true}
                  height="800px"
                  onChange={instance => {
                    const code = instance.getValue();
                    setConfigCode(code);
                    // Use the ref value which is always up to date
                    if (currentEnvRef.current === 0) {
                      setProdConfig(prev => ({ ...prev, latest: code }));
                    } else {
                      setStagingConfig(prev => ({ ...prev, latest: code }));
                    }
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle1" gutterBottom>
                  In-Use Configuration
                </Typography>
                <CodeEditor
                  value={diffConfigCode}
                  editable={false}
                  height="800px"
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={handleSave}>
          {currentSchema.type === "config" ? "Save Config" : "Save Script"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SchemaConfigDialog;
